import { createStore } from 'zustand/vanilla';

const createAuthStore = (supabaseClient) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required to initialize the auth store');
  }


  try {
    const store = createStore((set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Login function
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        
        try {
          let userData = null;
          let isSubUser = false;
          let subUserData = null;

          // Try main tenant login
          const { data, error } = await supabaseClient
            .from('invoice_users')
            .select('*')
            .eq('email', email)
            .eq('password', password)
            .eq('is_active', true)
            .single();

          if (data) {
            userData = data;
          } else {
            // Try sub-user login
            const { data: subData, error: subError } = await supabaseClient
              .from('invoice_sub_users')
              .select('*, invoice_users(*)')
              .eq('email', email)
              .eq('password', password)
              .eq('is_active', true)
              .single();

            if (subError && subError.code === 'PGRST116') {
              throw new Error('Credenciales inválidas');
            } else if (subError) {
              throw subError;
            }

            if (!subData || !subData.invoice_users) {
              throw new Error('Usuario no encontrado o inactivo');
            }

            isSubUser = true;
            subUserData = subData;
            userData = subData.invoice_users; // The main tenant
          }

          // Store user data (excluding password)
          const { password: _, ...userWithoutPassword } = userData;
          
          if (isSubUser) {
            userWithoutPassword.sub_user_id = subUserData.id;
            userWithoutPassword.sub_user_email = subUserData.email;
            userWithoutPassword.original_first_name = userWithoutPassword.first_name;
            userWithoutPassword.original_last_name = userWithoutPassword.last_name;
            userWithoutPassword.first_name = subUserData.first_name || userWithoutPassword.first_name;
            userWithoutPassword.last_name = subUserData.last_name || userWithoutPassword.last_name;
            userWithoutPassword.is_sub_user = true;
          }

          set({
            user: userWithoutPassword,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });

          // Store in localStorage for persistence
          localStorage.setItem('invoice_user', JSON.stringify(userWithoutPassword));
          
          return { success: true, user: userWithoutPassword };
        } catch (error) {
          console.error('Login error:', error.message);
          set({
            error: error.message,
            isLoading: false,
            isAuthenticated: false,
            user: null
          });
          return { success: false, error: error.message };
        }
      },

      // Logout function
      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          error: null
        });
        localStorage.removeItem('invoice_user');
      },

      // Update current user
      updateUser: (updates) => {
        const currentUser = get().user;
        if (currentUser) {
          const updatedUser = { ...currentUser, ...updates };
          set({ user: updatedUser });
          localStorage.setItem('invoice_user', JSON.stringify(updatedUser));
        }
      },

      // Initialize from localStorage
      initializeAuth: () => {
        try {
          const storedUser = localStorage.getItem('invoice_user');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            set({
              user,
              isAuthenticated: true,
              error: null
            });
            return true;
          }
        } catch (error) {
          console.error('Error initializing auth from localStorage:', error);
          localStorage.removeItem('invoice_user');
        }
        return false;
      },

      // Validate current session
      validateSession: async () => {
        const { user } = get();
        if (!user) return false;

        try {
          let data, error;
          if (user.is_sub_user) {
            const result = await supabaseClient
              .from('invoice_sub_users')
              .select('id, is_active')
              .eq('id', user.sub_user_id)
              .single();
            data = result.data;
            error = result.error;
          } else {
            const result = await supabaseClient
              .from('invoice_users')
              .select('id, is_active')
              .eq('id', user.id)
              .single();
            data = result.data;
            error = result.error;
          }

          if (error || !data || !data.is_active) {
            get().logout();
            return false;
          }

          return true;
        } catch (error) {
          console.error('Session validation error:', error);
          get().logout();
          return false;
        }
      },

      // Get current user ID
      getUserId: () => {
        const { user } = get();
        return user?.id || null;
      },

      // Fetch tenant document types
      tenantDocTypes: [],
      fetchTenantDocTypes: async () => {
        const { user } = get();
        if (!user || !user.enable_custom_doc_types) {
          set({ tenantDocTypes: [] });
          return;
        }

        try {
          // Use user.id which is the tenant ID (or main tenant ID if sub-user)
          const tenantId = user.id;
          const { data, error } = await supabaseClient
            .from('invoice_tenant_doc_types')
            .select('*')
            .eq('tenant_user_id', tenantId);
            
          if (error) throw error;
          set({ tenantDocTypes: data || [] });
        } catch (error) {
          console.error('Error fetching tenant doc types:', error);
          set({ tenantDocTypes: [] });
        }
      },

      // Fetch doc type to warehouse mappings
      docTypeWarehouses: [],
      fetchDocTypeWarehouses: async () => {
        const { user } = get();
        if (!user || !user.enable_custom_doc_types) {
          set({ docTypeWarehouses: [] });
          return;
        }

        try {
          const tenantId = user.id;
          const { data, error } = await supabaseClient
            .from('invoice_custom_doc_type_warehouses')
            .select('*')
            .eq('tenant_user_id', tenantId);
            
          if (error) throw error;
          set({ docTypeWarehouses: data || [] });
        } catch (error) {
          console.error('Error fetching doc type warehouses:', error);
          set({ docTypeWarehouses: [] });
        }
      },

      // Clear error
      clearError: () => set({ error: null })
    }));

    return store;
  } catch (error) {
    console.error('Error creating auth store:', error);
    throw error;
  }
};

export default createAuthStore;
